import React from 'react';
import { cn } from '@/lib/utils';

export interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: 'default' | 'flat' | 'outlined';
  padding?: 'none' | 'sm' | 'md' | 'lg';
  hover?: boolean;
}

const Card = React.forwardRef<HTMLDivElement, CardProps>(
  ({ className, variant = 'default', padding = 'md', hover = false, children, ...props }, ref) => {
    const variants = {
      // A white section raised off the canvas: one clear edge and the faintest
      // shadow. `card-border` follows the theme (see globals.css).
      default: 'bg-white dark:bg-gray-900 rounded-xl shadow-card border border-card-border',
      flat: 'bg-gray-50 dark:bg-gray-800 rounded-xl',
      outlined: 'bg-white dark:bg-gray-900 rounded-xl border border-card-border',
    };

    const paddings = {
      none: '',
      sm: 'p-3',
      md: 'p-5',
      lg: 'p-6',
    };

    return (
      <div
        ref={ref}
        className={cn(
          variants[variant],
          paddings[padding],
          hover && 'hover:shadow-card-hover hover:border-gray-300 dark:hover:border-gray-600 transition-[box-shadow,border-color] duration-200',
          className
        )}
        {...props}
      >
        {children}
      </div>
    );
  }
);

Card.displayName = 'Card';

// Card Header
export interface CardHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  title?: string;
  description?: React.ReactNode;
  action?: React.ReactNode;
  icon?: React.ReactNode;
}

const CardHeader = React.forwardRef<HTMLDivElement, CardHeaderProps>(
  ({ className, title, description, action, icon, children, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn('flex items-start justify-between mb-4', className)}
        {...props}
      >
        <div className="flex items-start">
          {icon && <div className="flex-shrink-0 mr-3">{icon}</div>}
          <div>
            {title && <h3 className="text-[15px] font-semibold leading-tight text-gray-900 dark:text-gray-100">{title}</h3>}
            {description && (
              typeof description === 'string'
                ? <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{description}</p>
                : <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">{description}</div>
            )}
            {children}
          </div>
        </div>
        {action && <div className="flex-shrink-0">{action}</div>}
      </div>
    );
  }
);

CardHeader.displayName = 'CardHeader';

// Card Content
const CardContent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => {
    return <div ref={ref} className={cn('', className)} {...props} />;
  }
);

CardContent.displayName = 'CardContent';

export { Card, CardHeader, CardContent };
